import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, Check, CheckCircle2, Star, Wrench } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import supabase from '../lib/supabase';
import usePageTitle from '../hooks/usePageTitle';
import '../styles/pages/AccountPage.css';

const formatDate = (dateString) => {
  if (!dateString) return 'Not set';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric'
  });
};

const formatDateShort = (dateString) => {
  if (!dateString) return 'Not set';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric'
  });
};

const AccountPage = () => {
  usePageTitle('My Account');
  const { user, profile, loading: authLoading, isFreeMode } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [subscriptionError, setSubscriptionError] = useState(null);

  const [feedbackItems, setFeedbackItems] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(true);
  const [feedbackError, setFeedbackError] = useState(null);

  const [planCount, setPlanCount] = useState(0);
  const [daysUsed, setDaysUsed] = useState(0);
  const [statsLoading, setStatsLoading] = useState(true);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [deletionRequested, setDeletionRequested] = useState(false);

  const handleRequestDeletion = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const { error } = await supabase.rpc('mark_account_pending_deletion');
      if (error) throw error;
      setDeletionRequested(true);
      setDeleteModalOpen(false);
      setDeleteConfirmText('');
      // Sign out after a brief moment so the user sees the confirmation
      setTimeout(async () => {
        await supabase.auth.signOut();
        navigate('/', { state: { deletionPending: true } });
      }, 3500);
    } catch (e) {
      setDeleteError(e.message || 'Could not submit deletion request. Please contact support.');
      setDeleting(false);
    }
  };

  const memberSince = profile?.created_at
    ? formatDate(profile.created_at)
    : 'Not set';

  const authType = profile?.auth_provider === 'google' || profile?.auth_provider === 'google_oauth2'
    ? 'Google / Gmail'
    : 'Email & Password';

  useEffect(() => {
    if (authLoading || !user?.id) return;

    const fetchAll = async () => {
      // Subscription
      setSubscriptionLoading(true);
      try {
        const { data, error } = await supabase
          .from('subscriptions')
          .select('id, status, product_name, current_period_end, cancel_at_period_end')
          .eq('user_id', user.id)
          .in('status', ['active'])
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (error) throw error;
        setSubscription(data || null);
      } catch (e) {
        setSubscriptionError(e.message);
      } finally {
        setSubscriptionLoading(false);
      }

      // Feedback
      setFeedbackLoading(true);
      try {
        const { data, error } = await supabase
          .from('feedback')
          .select('id, created_at, feedback_type, message, status')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
        if (error) throw error;
        setFeedbackItems(data || []);
      } catch (e) {
        setFeedbackError(e.message);
      } finally {
        setFeedbackLoading(false);
      }

      // Stats: plan count + distinct days used
      setStatsLoading(true);
      try {
        const [{ data: plans }, { data: events }] = await Promise.all([
          supabase.from('projects').select('id').eq('user_id', user.id),
          supabase.from('usage_events')
            .select('created_at')
            .eq('user_id', user.id)
            .in('event_type', ['copy_results', 'export_excel'])
        ]);
        setPlanCount(plans?.length || 0);
        const distinctDays = new Set((events || []).map(e => e.created_at?.slice(0, 10)));
        setDaysUsed(distinctDays.size);
      } catch (e) {
        // non-critical, leave at 0
      } finally {
        setStatsLoading(false);
      }
    };

    fetchAll();
  }, [user, authLoading]);

  if (authLoading) {
    return <div className="acct-loading">Loading account details...</div>;
  }

  if (!user) {
    return (
      <div className="acct-page">
        <p>Please log in to view your account details.</p>
      </div>
    );
  }

  const displayName = profile?.full_name || user.email;

  return (
    <div className="acct-page">

      {/* Header */}
      <div className="acct-header">
        <div className="acct-avatar">{(profile?.full_name || user.email || '?')[0].toUpperCase()}</div>
        <div className="acct-header-info">
          <h1 className="acct-name">{displayName}</h1>
          <p className="acct-email">{user.email}</p>
          <div className="acct-badges">
            {profile?.is_admin && <span className="acct-badge acct-badge-admin">Admin</span>}
            {profile?.has_lifetime_access && <span className="acct-badge acct-badge-lifetime">Lifetime Member</span>}
            {subscription && <span className="acct-badge acct-badge-sub">Pro Subscriber</span>}
            <span className="acct-badge acct-badge-auth">{authType}</span>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="acct-stats">
        <div className="acct-stat">
          <span className="acct-stat-num">{statsLoading ? '…' : planCount}</span>
          <span className="acct-stat-label">Icing Plans</span>
        </div>
        <div className="acct-stat">
          <span className="acct-stat-num">{statsLoading ? '…' : daysUsed}</span>
          <span className="acct-stat-label">Active Days</span>
        </div>
        <div className="acct-stat">
          <span className="acct-stat-num">{statsLoading ? '…' : feedbackItems.length}</span>
          <span className="acct-stat-label">Feedback Sent</span>
        </div>
        <div className="acct-stat">
          <span className="acct-stat-num acct-stat-date">{memberSince}</span>
          <span className="acct-stat-label">Member Since</span>
        </div>
      </div>

      {/* Pro Access Status */}
      <div className="acct-card">
        <h2 className="acct-card-title">Pro Access</h2>

        {profile?.has_lifetime_access && (
          <div className="acct-access-row acct-access-lifetime">
            <Star className="acct-access-icon" size={20} fill="currentColor" />
            <div>
              <strong>Lifetime Member</strong>
              <p>You have permanent Pro access. Thank you for your support!</p>
            </div>
          </div>
        )}

        {profile?.is_admin && (
          <div className="acct-access-row acct-access-admin">
            <Wrench className="acct-access-icon" size={20} />
            <div>
              <strong>Admin Account</strong>
              <p>Full Pro access granted via admin privileges.</p>
            </div>
          </div>
        )}

        {isFreeMode && !profile?.has_lifetime_access && !profile?.is_admin && (
          <div className="acct-access-row acct-access-free">
            <div>
              <strong>Pro Access: Free</strong>
              <p>
                All Pro features, including the Pro Calculator and the AI Color Mix Guide,
                are free with your account. Enjoy!
              </p>
              <a href="/pro" className="acct-trial-started-link">Open the Pro Calculator →</a>
            </div>
          </div>
        )}

        {subscriptionError && (
          <p className="acct-error">Could not load subscription: {subscriptionError}</p>
        )}

        {!subscriptionLoading && subscription && subscription.status === 'active' && (
          <div className="acct-access-row acct-access-sub">
            <Check className="acct-access-icon" size={20} />
            <div>
              <strong>Pro Subscription: Active</strong>
              <p>
                Plan: {subscription.product_name || 'Pro Annual'}<br />
                {subscription.cancel_at_period_end
                  ? <span className="acct-cancel-warning">Cancels on {formatDate(subscription.current_period_end)}</span>
                  : <>Renews: {formatDate(subscription.current_period_end)}</>
                }
              </p>
            </div>
          </div>
        )}

      </div>

      {/* Account Info */}
      <div className="acct-card">
        <h2 className="acct-card-title">Account Info</h2>
        <div className="acct-info-grid">
          <div className="acct-info-row">
            <span className="acct-info-label">Name</span>
            <span className="acct-info-value">{profile?.full_name || 'Not set'}</span>
          </div>
          <div className="acct-info-row">
            <span className="acct-info-label">Email</span>
            <span className="acct-info-value">{user.email}</span>
          </div>
          <div className="acct-info-row">
            <span className="acct-info-label">Sign-in Method</span>
            <span className="acct-info-value">{authType}</span>
          </div>
          <div className="acct-info-row">
            <span className="acct-info-label">Member Since</span>
            <span className="acct-info-value">{memberSince}</span>
          </div>
        </div>
      </div>

      {/* Feedback Section */}
      <div className="acct-card">
        <h2 className="acct-card-title">My Feedback Submissions</h2>
        {feedbackError && <p className="acct-error">Error loading feedback: {feedbackError}</p>}
        {feedbackLoading && <p className="acct-muted">Loading…</p>}
        {!feedbackLoading && !feedbackError && feedbackItems.length === 0 && (
          <p className="acct-muted">You haven't submitted any feedback yet.</p>
        )}
        {!feedbackLoading && !feedbackError && feedbackItems.length > 0 && (
          <div className="acct-table-wrapper">
            <table className="acct-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Message</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {feedbackItems.map(item => (
                  <tr key={item.id}>
                    <td className="acct-td-date">{formatDateShort(item.created_at)}</td>
                    <td className="acct-td-type">{item.feedback_type}</td>
                    <td>{item.message}</td>
                    <td>
                      <span className={`acct-status acct-status-${item.status}`}>
                        {item.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Deletion Requested confirmation banner */}
      {deletionRequested && (
        <div className="acct-deletion-pending-banner">
          <CheckCircle2 className="acct-deletion-pending-icon" size={22} />
          <div>
            <strong>Deletion request received.</strong>
            <p>
              Our support staff will review and permanently delete your account within 7 days.
              You will receive a confirmation email once it has been fully removed from our servers.
              You are being signed out now…
            </p>
          </div>
        </div>
      )}

      {/* Danger Zone */}
      <div className="acct-card acct-card-danger">
        <h2 className="acct-card-title">Danger Zone</h2>
        <p className="acct-muted">
          Request permanent deletion of your account and all associated data, including saved icing plans,
          usage history, and subscription records. Our support staff will process this within 7 days
          and send you a confirmation email when complete.
        </p>
        <button className="acct-delete-btn" onClick={() => setDeleteModalOpen(true)}>
          Request Account Deletion
        </button>
      </div>

      {/* Request Deletion Modal */}
      {deleteModalOpen && (
        <div className="acct-modal-overlay" onClick={() => { setDeleteModalOpen(false); setDeleteConfirmText(''); setDeleteError(null); }}>
          <div className="acct-modal" onClick={e => e.stopPropagation()}>
            <div className="acct-modal-icon" style={{ display: 'flex', justifyContent: 'center' }}>
              <AlertTriangle size={48} />
            </div>
            <h2 className="acct-modal-title">Request Account Deletion?</h2>
            <p className="acct-modal-body">
              This will submit a deletion request to our support team. The following will be
              permanently removed within <strong>7 days</strong>:
            </p>
            <ul className="acct-modal-list">
              <li>Your account profile (email, name, sign-in history)</li>
              <li>All saved Icing Plans</li>
              <li>Your subscription record and usage history</li>
            </ul>
            <p className="acct-modal-body">
              You will be <strong>signed out immediately</strong> and will not be able to log in
              again. A confirmation email will be sent once your data has been fully deleted.
            </p>
            <p className="acct-modal-body">Type <strong>DELETE</strong> to confirm:</p>
            <input
              className="acct-delete-confirm-input"
              type="text"
              value={deleteConfirmText}
              onChange={e => setDeleteConfirmText(e.target.value)}
              placeholder="Type DELETE here"
              autoComplete="off"
            />
            {deleteError && <p className="acct-error">{deleteError}</p>}
            <div className="acct-modal-actions">
              <button
                className="acct-modal-confirm-btn acct-modal-confirm-btn-danger"
                onClick={handleRequestDeletion}
                disabled={deleteConfirmText !== 'DELETE' || deleting}
              >
                {deleting ? 'Submitting…' : 'Submit Deletion Request'}
              </button>
              <button
                className="acct-modal-cancel-btn"
                onClick={() => { setDeleteModalOpen(false); setDeleteConfirmText(''); setDeleteError(null); }}
                disabled={deleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default AccountPage;
